import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useGoogleMaps } from "contexts/GoogleMapsContext";
import { Country } from "country-state-city";
import useCustomToaster from "hooks/useCustomToaster";
import Papa from "papaparse";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { setRefetchBillingData } from "store/billingSlice";
import Preview from "./Preview";
import UploadViewSubSupplier from "./UploadViewSubSupplier";
import { getSubTiersList } from "services/subtiers.services";
import { toggleIsUploadBulkSubSupplierModalOpen } from "store/subSupplierSlice";
import { createBulkSubSupplier } from "services/sub_suppliers.services";

const UploadBulkSubSupplier = ({ closeModal, refetch }) => {
  const queryClient = useQueryClient();
  const { workspaceId } = useParams();
  const dispatch = useDispatch();
  const [isDragOver, setIsDragOver] = useState(false);
  const [preview, setPreview] = useState(false);
  const [subTiers, setSubTiers] = useState([]);
  const [uploadedData, setUploadedData] = useState(null);
  const [fileData, setFileData] = useState(null);
  const { showCustomToast } = useCustomToaster();
  const { refetchBillingData, workspacePlan } = useSelector((state) => state.billingSlice);
  const { isLoaded } = useGoogleMaps();
  const [autocompleteInstances, setAutocompleteInstances] = useState({});

  const handleCancel = () => {
    // reset();
    closeModal();
  };
  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    onFileDropOrChange(e);
  };
  const isDragEvent = (event) => {
    return "dataTransfer" in event;
  };
  const onFileDropOrChange = (e) => {
    let file = isDragEvent(e) ? e.dataTransfer.files[0] : e.target.files[0];

    if (file && (file.type === "text/csv" || file.name.endsWith(".csv"))) {
      const reader = new FileReader();

      reader.onload = (event) => {
        const firstLine = event.target.result.split("\n")[0];

        // Dynamic delimiter detection
        const semicolonCount = (firstLine.match(/;/g) || []).length;
        const commaCount = (firstLine.match(/,/g) || []).length;
        const delimiter = semicolonCount > commaCount ? ";" : ",";

        Papa.parse(file, {
          header: true,
          skipEmptyLines: "greedy",
          delimiter: delimiter,
          complete: function (results) {
            const { data } = results;

            const newData = data.map((item) => {
              const getVal = (key) => item[key] || "";

              // FIX: Removed .replace(/ /g, "-") to preserve spaces
              let partnerName = getVal("Partner Name").trim();

              let subTierRaw = getVal("Sub Tier");
              let street = getVal("Street");
              let city = getVal("City");
              let country = getVal("Country");

              // Process Sub Tier
              let matchedSubTiers = [];
              if (subTierRaw) {
                const labelsInCsv = subTierRaw
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean);

                matchedSubTiers = subTiers.filter(
                  (tier) => labelsInCsv.includes(tier.label) || labelsInCsv.includes(tier.value),
                );
              }

              // Process Country
              let countryData = Country.getAllCountries().find(
                (c) => c.name.toLowerCase() === country.trim().toLowerCase(),
              );

              let countryObject = countryData
                ? {
                    label: countryData.name,
                    value: countryData.isoCode,
                    ...countryData,
                  }
                : null;

              // Validation Errors
              const partnerNameError = !partnerName;
              const subTierError = matchedSubTiers.length === 0;
              const streetError = !street;
              const cityError = !city;
              const countryError = !countryObject;

              const fullAddress = [street, city, countryObject?.label || country]
                .filter(Boolean)
                .join(", ");

              return {
                partnerName: partnerName, // Now contains spaces as expected
                subTier: matchedSubTiers,
                street,
                city,
                country: countryObject,
                address: fullAddress,
                country_code: countryObject?.isoCode || "",
                error: {
                  partnerNameError,
                  subTierError,
                  streetError,
                  cityError,
                  countryError,
                },
              };
            });

            setUploadedData(newData);
          },
        });
      };

      reader.readAsText(file);
    } else {
      toast.error("Please upload a valid CSV file.");
    }
  };
  const handleFileDropOrChange = (e) => {
    let file = null;
    if (e.dataTransfer) {
      file = e.dataTransfer.files[0];
    } else {
      file = e.target.files[0];
    }
    if (file && file.type === "text/csv") {
      const fileSize = (file.size / (1024 * 1024)).toFixed(2) + " MB"; // Calculate file size in MB
      setFileData({
        name: file.name,
        size: fileSize,
      });
      onFileDropOrChange(e); // Continue with your existing file parsing logic
    } else {
      console.error("Please upload a valid CSV file.");
    }
  };

  const subTiersListFetch = async () => {
    const SubTiers = await getSubTiersList();
    if (SubTiers?.status === 200) {
      const subTierOptions = SubTiers?.data?.sub_tiers.map((tier) => ({
        label: tier.name,
        customLabel: tier.name,
        value: tier.id,
      }));
      setSubTiers(subTierOptions);
    }
  };

  const handleErrors = (index, name, hasError, errors) => ({
    ...errors,
    [`${name}Error`]: hasError,
  });

  const onChangeHandler = (index, name, value) => {
    const updatedData = [...uploadedData];
    const itemToUpdate = { ...updatedData[index] };

    if (name === "partnerName") {
      itemToUpdate.partnerName = value;
      itemToUpdate.error = handleErrors(index, name, !value, itemToUpdate.error);
    } else if (name === "subTier") {
      itemToUpdate.subTier = value;
      itemToUpdate.error = handleErrors(index, name, value.length === 0, itemToUpdate.error);
    } else if (["street", "city"].includes(name)) {
      itemToUpdate[name] = value;
      itemToUpdate.error = handleErrors(index, name, !value, itemToUpdate.error);
    } else if (name === "country") {
      if (value) {
        itemToUpdate.country = value;
        itemToUpdate.country_code = value.isoCode;
        itemToUpdate.error = handleErrors(index, name, false, itemToUpdate.error);
      } else {
        itemToUpdate.country = "";
        itemToUpdate.country_code = "";
        itemToUpdate.error = handleErrors(index, name, true, itemToUpdate.error);
      }
    }

    // Recalculate address if any address field changed
    if (["street", "city", "country"].includes(name)) {
      const { street, city, country } = itemToUpdate;
      const countryName = country?.label || (typeof country === "string" ? country : "");
      itemToUpdate.address = [street, city, countryName].filter(Boolean).join(", ");
    }

    updatedData[index] = itemToUpdate;

    setUploadedData(updatedData);
  };

  const onLoad = (autocomplete, index) => {
    setAutocompleteInstances((prev) => ({
      ...prev,
      [index]: autocomplete,
    }));
  };

  const createBulkUploadSubSupplierMutation = useMutation({
    mutationKey: ["bulkSubSupplier", "createBulkSubSupplier"],
    mutationFn: (data) => createBulkSubSupplier(data),
    onMutate: () => {
      showCustomToast(
        {
          title: "Uploading bulk sub-supplier....",
        },
        "createBulkSubsupplierUploadLoadingToast",
      );
    },
    onSuccess: (res) => {
      if (res) {
        toast.dismiss("createBulkSubsupplierUploadLoadingToast");
        dispatch(toggleIsUploadBulkSubSupplierModalOpen());
        refetch();
        if (!refetchBillingData) {
          dispatch(setRefetchBillingData(true));
        }
        showCustomToast({
          title: "Bulk sub-supplier uploaded successfully",
        });
        queryClient.invalidateQueries({ queryKey: ["bulkSubSupplier", "createBulkSubSupplier"] });
      }
    },
    onError: (error) => {
      if (error) {
        toast.dismiss("createBulkSubsupplierUploadLoadingToast");
        const errorResponse = error.response?.data;

        if (errorResponse?.duplicate_sub_suppliers) {
          const duplicateOrders = errorResponse.duplicate_sub_suppliers.join('", "');
          showCustomToast({
            title: `Sub-supplier upload failed\nError: sub-supplier id "${duplicateOrders}" already exists`,
          });
        } else if (errorResponse?.errors) {
          Object.entries(errorResponse.errors).forEach(([field, messages]) => {
            messages.forEach((message) => {
              showCustomToast({
                title: message,
              });
            });
          });
        } else {
          showCustomToast({
            title: errorResponse?.message || error.message,
          });
        }
      }
    },
    onSettled: () => {
      toast.dismiss("createBulkSubsupplierUploadLoadingToast");
    },
  });

  const onSubmitBulkUpload = async () => {
    const csvData = uploadedData.map((item) => {
      const { partnerName, subTier, street, city, country, address } = item;
      const tempSubTier = subTier.map((item) => item.label);
      return {
        "Partner Name": partnerName,
        "Sub Tier": tempSubTier.toString(),
        Address: address,
        Street: street,
        City: city,
        Country: country?.label || "",
      };
    });
    const csv = Papa.unparse(csvData);
    const csvBlob = new Blob([csv], { type: "text/csv" });
    const formData = new FormData();
    formData.append("import_csv", csvBlob, "sub_suppliers.csv");
    formData.append("workspace_id", parseInt(workspaceId));
    if (formData) {
      createBulkUploadSubSupplierMutation.mutate(formData);
    }
  };

  const onPlaceChanged = (index) => {
    const autocomplete = autocompleteInstances[index];
    if (autocomplete) {
      const place = autocomplete.getPlace();
      const updatedData = [...uploadedData];
      let street = updatedData[index].street;
      let city = updatedData[index].city;
      let country = updatedData[index].country?.label || "";
      let countryCode = updatedData[index].country?.isoCode || "";

      if (place.address_components) {
        let foundStreet = false;
        let foundCity = false;
        let foundCountry = false;

        place.address_components.forEach((component) => {
          const types = component.types;
          if (types.includes("locality") || types.includes("administrative_area_level_1")) {
            city = component.long_name;
            foundCity = true;
          }
          if (types.includes("country")) {
            country = component.long_name;
            countryCode = component.short_name;
            foundCountry = true;
          }
        });

        // The street address is not always broken down into 'street_number' and 'route'.
        // It's often found in the 'name' of the place object when it's a specific establishment,
        // or in the first address component. A more reliable way is to reconstruct it.
        const streetNumber =
          place.address_components.find((c) => c.types.includes("street_number"))?.long_name || "";
        const route =
          place.address_components.find((c) => c.types.includes("route"))?.long_name || "";

        if (streetNumber || route) {
          street = `${streetNumber} ${route}`.trim();
          foundStreet = true;
        }

        if (!foundStreet && (foundCity || foundCountry)) {
          street = "";
        }

        if (!foundCity) {
          city = "";
        }

        let countryData = Country.getCountryByCode(countryCode);
        if (!countryData) {
          countryData = Country.getAllCountries().find(
            (c) => c.name.toLowerCase() === country?.toLowerCase(),
          );
        }
        let countryObject = null;
        if (countryData) {
          countryObject = { label: countryData.name, value: countryData.isoCode, ...countryData };
        }

        updatedData[index] = {
          ...updatedData[index],
          street: street,
          city: city,
          country: countryObject,
          address: place.formatted_address || [street, city, country].filter(Boolean).join(", "),
          country_code: countryData?.isoCode || countryCode,
          error: {
            ...updatedData[index].error,
            streetError: !street,
            countryError: !country,
          },
        };

        setUploadedData(updatedData);
      }
    }
  };

  useEffect(() => {
    if (isLoaded) {
      subTiersListFetch();
    }
  }, [isLoaded]);

  const hasAnyError = () => {
    if (uploadedData) {
      return uploadedData?.some((data) => Object?.values(data?.error)?.includes(true));
    } else {
      return true;
    }
  };
  const handleDownloadFunction = () => {
    const url = `/sub_suppliers.csv`;
    const link = document.createElement("a");
    link.href = url;
    link.download = "sub_suppliers.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="">
      <div className="rounded-xl bg-white">
        {/* min-w-[749px] */}
        <div className="grid grid-cols-1 gap-4">
          <div className="w-full py-6 overflow-x-hidden">
            {!preview && (
              <>
                <UploadViewSubSupplier
                  isDragOver={isDragOver}
                  handleDragEnter={handleDragEnter}
                  handleDragLeave={handleDragLeave}
                  handleDrop={handleDrop}
                  onFileDropOrChange={onFileDropOrChange}
                  uploadedData={uploadedData}
                  setPreview={setPreview}
                  handleFileDropOrChange={handleFileDropOrChange}
                  setFileData={setFileData}
                  fileData={fileData}
                  handleDownloadFunction={handleDownloadFunction}
                  closeModal={closeModal}
                  handleCancel={handleCancel}
                  preview={preview}
                  onSubmitBulkUpload={onSubmitBulkUpload}
                  hasAnyError={hasAnyError}
                />
              </>
            )}
            {preview && (
              <Preview
                data={uploadedData}
                subTiers={subTiers}
                onChangeHandler={onChangeHandler}
                onSubmit={onSubmitBulkUpload}
                closeModal={closeModal}
                handleCancel={handleCancel}
                uploadedData={uploadedData}
                preview={preview}
                setPreview={setPreview}
                onSubmitBulkUpload={onSubmitBulkUpload}
                hasAnyError={hasAnyError}
                onLoad={onLoad}
                onPlaceChanged={onPlaceChanged}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadBulkSubSupplier;
